import { type FC, type ReactNode, useId, useRef, useState } from "react";
import { useFormState } from "react-hook-form";
import { FileUploadController } from "react-hook-form-file-upload";
import { twMerge } from "tailwind-merge";

import { ErrorMessageBanner } from "./ErrorMessageBanner";

import { collaborationUploadFile } from "api/api-worksheets";
import { t } from "i18n/i18n";

type LabeledFileUploadControllerProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "children"
> & {
  wrapperClassName?: string;
  name: string;
  label: string;
  postLabel?: ReactNode;
  required?: boolean;
  validateFiles?: Parameters<typeof FileUploadController>[0]["validateFiles"];
  children:
    | ReactNode
    | ((props: {
        value: string | null;
        select: () => void;
        remove: () => void;
        isUploading: boolean;
      }) => ReactNode);
};

export const LabeledFileUploadController: FC<LabeledFileUploadControllerProps> = ({
  id: idProp,
  className,
  wrapperClassName,
  name,
  label,
  postLabel,
  required = false,
  disabled = false,
  validateFiles,
  children,
  ...inputProps
}) => {
  const randomId = useId();
  const formState = useFormState({ name });
  const id = idProp || `${randomId}-${name}`;
  const errorId = `${id}-error`;

  const [isUploading, setIsUploading] = useState(false);
  const [isDragHover, setIsDragHover] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const labelNode = (
    <label className="font-medium text-black-tint-20" htmlFor={id}>
      {label}
    </label>
  );

  return (
    <div className={twMerge("flex flex-col", wrapperClassName)}>
      <div className="flex items-center justify-between">
        {postLabel ? (
          <div className="flex items-center gap-2">
            {labelNode}
            {postLabel}
          </div>
        ) : (
          labelNode
        )}
        {!required && <div className="font-normal text-black-tint-40">{t("common.optional")}</div>}
      </div>
      <FileUploadController
        name={name}
        render={({ value, field, select, remove, getDropZoneProps }) => (
          <div
            {...getDropZoneProps({
              onDragEnter: () => setIsDragHover(true),
              onDragLeave: (event) => {
                // `dragleave` is also called when dragging over child elements of the dropzone,
                // so we can't simply call `setIsDragHover(false)` here -- otherwise, dragging
                // over a child would cancel the drag hover! Instead, we can check the mouse position
                // and see if we have actually left the dropzone before calling `setIsDragHover(false)`.
                const dropZoneEl = dropZoneRef.current;
                if (!dropZoneEl) return;
                const dropZoneRect = dropZoneEl.getBoundingClientRect();
                if (
                  event.clientY < dropZoneRect.top ||
                  event.clientY >= dropZoneRect.bottom ||
                  event.clientX < dropZoneRect.left ||
                  event.clientX >= dropZoneRect.right
                ) {
                  setIsDragHover(false);
                }
              },
              onDrop: () => setIsDragHover(false),
            })}
            className={twMerge("group", className)}
            data-disabled={disabled || undefined}
            data-drag-hover={isDragHover || undefined}
            ref={dropZoneRef}
          >
            <input
              {...field}
              aria-errormessage={formState.errors[name] ? errorId : undefined}
              aria-invalid={formState.errors[name] ? "true" : "false"}
              className="hidden"
              disabled={disabled}
              id={id}
              {...inputProps}
            />
            {typeof children === "function"
              ? children({
                  value,
                  select,
                  remove,
                  isUploading,
                })
              : children}
          </div>
        )}
        rules={{ required: required ? t("error.field_required") : false }}
        uploadFiles={async (files) => {
          setIsUploading(true);
          try {
            const resp = await collaborationUploadFile(files[0]);
            return resp.url;
          } finally {
            setIsUploading(false);
          }
        }}
        validateFiles={validateFiles}
        droppable
      />
      <ErrorMessageBanner id={errorId} name={name} />
    </div>
  );
};
